import React from "react";

import { cx, css } from "@emotion/css";
import TextField from "@mui/material/TextField";

import { useStyles } from "@/hooks/useStyles.ts";
import { HTMLAutocomplete } from "@/uiKit/atoms/inputs/AutoComplete.ts";
import { AtomProps, type StyleClasses } from "@/uiKit/atoms/utils/AtomTypes.ts";

// All input types that are text fields (forbids radio, checkbox, date, ...)
export type TextFieldType = "email" | "hidden" | "month" | "password" | "search" | "tel" | "text" | "url" | "week";

export type BaseTextInputProps = {
  value: string | undefined;
  label?: string;
  type?: TextFieldType;
  autoComplete?: HTMLAutocomplete;
  onChange?: (value: string | undefined, event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  autoFocus?: boolean;
  multiline?: boolean;
  minRows?: number;
  maxRows?: number;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  fullWidth?: boolean;
};

export type TextInputProps = AtomProps<BaseTextInputProps, StyleClasses<"root" | "input">>;

export const TextInput: React.FC<TextInputProps> = ({
  value,
  onChange,
  onBlur,
  onFocus,
  error,
  startAdornment,
  endAdornment,
  className,
  classes,
  ...props
}) => {
  const styles = useStyles(makeStyles);

  return (
    <TextField
      value={value ?? ""}
      onChange={event => onChange?.(event.target.value ? event.target.value : undefined, event)}
      onBlur={event => onBlur?.(event)}
      onFocus={event => onFocus?.(event)}
      className={cx("scrollbar", className)}
      variant={props.multiline ? "outlined" : "standard"}
      error={error}
      classes={{
        root: cx(styles.root, classes?.root),
      }}
      slotProps={{
        input: {
          startAdornment,
          endAdornment,
          classes: {
            input: cx({ [styles.input]: true, [styles.fullWidth]: props.fullWidth }, classes?.input),
          },
        },
      }}
      {...props}
    />
  );
};

const makeStyles = () => ({
  root: css({}),
  input: css({
    MozAppearance: "textfield",
    WebkitAppearance: "textfield",
    margin: 0,
    width: "fit-content",
    minWidth: 0,
  }),
  fullWidth: css({
    width: "100%",
  }),
});
